import moment from "moment";
import LoadingSkeleton from "@shared/LoadingSkeleton";
import { getTitleCase } from "@utils/utils";
import { datasetLabel } from "./utils";
import type { RecentSearchResponse, RecentSearchRow } from "./types";

type ProductRecentSearchesProps = {
  recentSearchData?: RecentSearchResponse;
  isLoading: boolean;
  onReload: (row: RecentSearchRow) => void;
};

const directionLabel = (companyType?: string) => (companyType === "shipper" ? "Suppliers" : "Buyers");

const ProductRecentSearches = ({ recentSearchData, isLoading, onReload }: ProductRecentSearchesProps) => {
  return (
    <div className="tw-flex-1 tw-min-h-0 tw-pt-[19px] tw-pr-[13px] tw-pb-3 tw-pl-3 tw-rounded-lg tw-shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] tw-border tw-border-gray-200 tw-bg-white tw-overflow-auto">
      <div className="tw-space-y-4 tw-h-full">
        {recentSearchData?.data && Array.isArray(recentSearchData.data) && recentSearchData.data.length ? (
          recentSearchData.data.map((group) => (
            <div key={group.created_at} className="tw-gap-2 tw-flex tw-flex-col">
              <div className="tw-font-medium tw-text-xs tw-text-gray-500 tw-border-b tw-border-gray-200 tw-pb-1">{moment(group.created_at).format("MMM DD, YYYY")}</div>
              {group.row_data.map((row, index) => {
                const userSearch = (row.search || []).join(", ");
                return (
                  <div key={`${userSearch}_${row.company_type}_${row.dataset_type}_${index}`} className="tw-border tw-border-gray-200 tw-px-2 tw-py-3 tw-rounded-lg tw-flex tw-justify-between tw-items-center">
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <span className="tw-text-sm tw-font-medium tw-text-gray-max">{userSearch.length > 45 ? `${getTitleCase(userSearch.slice(0, 45))}...` : getTitleCase(userSearch)}</span>
                      <span className="tw-text-sm tw-font-normal tw-text-gray-900">
                        {directionLabel(row.company_type)}
                        {datasetLabel(row.dataset_type) ? (
                          <>
                            {" "}
                            <span className="tw-text-gray-500">&middot;</span> {datasetLabel(row.dataset_type)}
                          </>
                        ) : null}
                      </span>
                    </div>
                    <div onClick={() => onReload(row)} className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-primary-purple-600 hover:tw-bg-gray-200 tw-flex tw-items-center tw-text-center tw-cursor-pointer tw-gap-1">
                      Reload
                      <svg xmlns="http://www.w3.org/2000/svg" height="14px" viewBox="0 -960 960 960" width="14px" fill="#7143f1">
                        <path d="M348.87-116.6q-61.14-26.6-106.4-71.87-45.27-45.26-71.87-106.4Q144-356 144-426h72q0 109 77.5 186.5T480-162q109 0 186.5-77.5T744-426q0-109-77.5-186.5T479.61-690H474l51 51-51 51-138-138 138-138 51 51-52 51h7q70 0 131.13 26.6 61.14 26.6 106.4 71.87 45.27 45.26 71.87 106.4Q816-496 816-426t-26.6 131.13q-26.6 61.14-71.87 106.4-45.26 45.27-106.4 71.87Q550-90 480-90t-131.13-26.6Z" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        ) : isLoading ? (
          <LoadingSkeleton lines={5} lineHeight="70px" width="100%" shape="rectangle" />
        ) : (
          <div className="tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-text-center tw-px-6">
            <div className="tw-w-14 tw-h-14 tw-rounded-2xl tw-bg-primary-purple-50 tw-flex tw-items-center tw-justify-center tw-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" height="28px" viewBox="0 -960 960 960" width="28px" fill="#7143f1">
                <path d="m618.92-298.92 42.16-42.16L510-492.16V-680h-60v212.15l168.92 168.93ZM480.07-100q-78.84 0-148.21-29.92t-120.68-81.21q-51.31-51.29-81.25-120.63Q100-401.1 100-479.93q0-78.84 29.92-148.21t81.21-120.68q51.29-51.31 120.63-81.25Q401.1-860 479.93-860q78.84 0 148.21 29.92t120.68 81.21q51.31 51.29 81.25 120.63Q860-558.9 860-480.07q0 78.84-29.92 148.21t-81.21 120.68q-51.29 51.31-120.63 81.25Q558.9-100 480.07-100Z" />
              </svg>
            </div>
            <div>
              <p className="tw-font-semibold tw-text-sm tw-text-gray-900 tw-m-0">No recent searches yet</p>
              <p className="tw-text-sm tw-text-gray-500 tw-mt-1 tw-mb-0">Run a search and it'll show up here so you can pick up right where you left off.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductRecentSearches;
